import React, { useState, useContext, useRef, useEffect } from 'react'
import { DAGTask } from '@pollination-solutions/pollination-sdk'
import { Node } from '~/types'
import { graphics } from '~/utils'
import { context } from '~/state/detailContext'

export interface RecipeTaskNodeProps extends Node.Props {
  task: DAGTask
}

export const RecipeTaskNode: React.FunctionComponent<RecipeTaskNodeProps> = ({ position, label, task }) => {
  const [x, y] = position
  const isLoop = (task?.loop ?? null) !== null

  const { reference, setNodeReference } = useContext(context)
  const [cardIsOpen, setCardIsOpen] = useState(false)
  const nodeRef = useRef<SVGCircleElement>(null)

  useEffect(() => {
    if (!reference) {
      setCardIsOpen(false)
    }
  }, [reference, setCardIsOpen])

  const handlePointerEnter = (): void => {
    const target = nodeRef.current

    if (!target) {
      return
    }

    const { left, top } = target.getBoundingClientRect()

    const position: [number, number] = [left - 30, top + 0.5]

    setNodeReference({ type: 'task', name: task.name, position: position, task: task })
    setCardIsOpen(true)
  }

  const handlePointerLeave = (): void => {
    setNodeReference(undefined)
  }

  const nameLabel = graphics.getTruncatedLabel(label)
  const templateLabel = graphics.getTruncatedLabel(task.template.split('/')[1])

  return isLoop ? (
    <g
      transform={`translate(${x}, ${y})`}
      style={{ overflow: 'visible' }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <circle r={20} stroke="none" fill="none" ref={nodeRef} />
      <rect
        width={174}
        height={32}
        ry={16}
        transform="translate(-87, -16)"
        fill="#FFF"
        stroke="#333"
        strokeWidth="2px"
      />
      <g transform="translate(-60, 0)">
        <rect ry={17} width={56} height={34} transform="translate(-28, -17)" fill="#C4C4C4" />
        <rect
          ry={16}
          width={54}
          height={32}
          transform="translate(-27, -16)"
          fill="none"
          stroke="#333"
          strokeWidth="2px"
        />
        <circle r={10} transform="translate(11, 0)" fill="#FFF" stroke="#C4C4C4" strokeWidth="2px" />
        <circle r={10} fill="#FFF" stroke="#C4C4C4" strokeWidth="2px" />
        <circle r={10} transform="translate(-11, 0)" fill="#FFF" stroke="#C4C4C4" strokeWidth="2px" />
      </g>
      {cardIsOpen ? null : (
        <>
          <g transform={'translate(-26, -2.5)'}>
            <text className="recipe-node-label" textAnchor="start" fontSize={12}>
              {nameLabel}
            </text>
          </g>
          <g transform={'translate(-26, 9.5)'}>
            <text className="recipe-node-detail" textAnchor="start">
              {templateLabel}
            </text>
          </g>
        </>
      )}
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono&display=swap');

        .recipe-node-label {
          font-family: 'Inter', sans-serif;
          font-weight: 300;
          font-size: 10px;
          fill: #333;
        }

        .recipe-node-detail {
          font-family: 'Space Mono', monospace;
          font-weight: 300;
          font-size: 8.5px;
        }
      `}</style>
    </g>
  ) : (
    <g
      transform={`translate(${x}, ${y})`}
      style={{ overflow: 'visible' }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <circle r={20} fill="none" stroke="none" ref={nodeRef} />
      <rect
        width={158}
        height={32}
        ry={16}
        transform="translate(-79, -16)"
        fill="#FFF"
        stroke="#333"
        strokeWidth="2px"
      />
      <g transform="translate(-64, 0)">
        <circle r={17} fill="#C4C4C4" className="recipe-node-background" />
        <circle r={16} fill="none" stroke="#333" strokeWidth="2px" />
        <circle r={9} fill="#FFF" stroke="none" />
      </g>
      {cardIsOpen ? null : (
        <>
          <g transform={'translate(-40, -2.5)'}>
            <text className="recipe-node-label" textAnchor="start" fontSize={12}>
              {nameLabel}
            </text>
          </g>
          <g transform={'translate(-40, 9.5)'}>
            <text className="recipe-node-detail" textAnchor="start">
              {templateLabel}
            </text>
          </g>
        </>
      )}
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono&display=swap');

        .recipe-node-label {
          font-family: 'Inter', sans-serif;
          font-weight: 300;
          font-size: 10px;
          fill: #333;
        }

        .recipe-node-detail {
          font-family: 'Space Mono', monospace;
          font-weight: 300;
          font-size: 8.5px;
        }
      `}</style>
    </g>
  )
}

export default RecipeTaskNode
